import type { TFunction } from 'i18next';
import type { MutableRefObject } from 'react';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { getIceServers, waitForIceGatheringComplete } from './webrtcStunner';

export type VoiceAgentWebRtcRefs = {
  peerConnectionRef: MutableRefObject<RTCPeerConnection | null>;
  audioRef: MutableRefObject<HTMLAudioElement | null>;
  localStreamRef: MutableRefObject<MediaStream | null>;
  /** Optional — set when a pipecat data channel opens so the UI can reply to the agent. */
  dataChannelRef?: MutableRefObject<RTCDataChannel | null>;
  /** Optional queue drained when the channel opens (mid-call sends before open). */
  pendingDataChannelMessagesRef?: MutableRefObject<string[]>;
};

const DATA_CHANNEL_OPEN_TIMEOUT_MS = 15000;

function flushPendingDataChannelMessages(
  refs: VoiceAgentWebRtcRefs,
  logContext: string
): void {
  const channel = refs.dataChannelRef?.current;
  const pending = refs.pendingDataChannelMessagesRef?.current;
  if (!channel || channel.readyState !== 'open' || !pending?.length) return;
  while (pending.length > 0) {
    const payload = pending.shift();
    if (!payload) continue;
    try {
      channel.send(payload);
      console.log(`${logContext} data channel send (queued)`, payload);
    } catch (err) {
      console.warn(`${logContext} queued data channel send failed`, err);
      pending.unshift(payload);
      break;
    }
  }
}

function waitForDataChannelOpen(
  channel: RTCDataChannel,
  timeoutMs = DATA_CHANNEL_OPEN_TIMEOUT_MS
): Promise<boolean> {
  if (channel.readyState === 'open') return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      channel.removeEventListener('open', onOpen);
      channel.removeEventListener('close', onClose);
      globalThis.clearTimeout(timer);
      resolve(ok);
    };
    const onOpen = () => finish(true);
    const onClose = () => finish(false);
    const timer = globalThis.setTimeout(() => finish(channel.readyState === 'open'), timeoutMs);
    channel.addEventListener('open', onOpen);
    channel.addEventListener('close', onClose);
  });
}

export type PostWebRtcSessionMutation = (args: {
  assistant_id: string;
  phonenumber?: string;
  customer_details?: {
    extra?: Record<string, unknown>;
    name?: string;
    email?: string;
    history?: string;
  };
}) => { unwrap: () => Promise<{ session_id?: string } | undefined> };

export type PostOfferMutation = (args: {
  sdp: string;
  type: string;
  session_id: string;
}) => {
  unwrap: () => Promise<{
    sdp?: string;
    type?: string;
    answer?: { sdp?: string; type?: string };
  }>;
};

export function cleanupVoiceAgentWebRtc(
  refs: VoiceAgentWebRtcRefs,
  resetState?: () => void
): void {
  refs.localStreamRef.current?.getTracks().forEach((tr) => tr.stop());
  refs.localStreamRef.current = null;
  refs.peerConnectionRef.current?.close();
  refs.peerConnectionRef.current = null;
  if (refs.dataChannelRef) {
    refs.dataChannelRef.current = null;
  }
  if (refs.pendingDataChannelMessagesRef) {
    refs.pendingDataChannelMessagesRef.current = [];
  }
  if (refs.audioRef.current) {
    refs.audioRef.current.srcObject = null;
  }
  resetState?.();
}

export type VoiceAgentTranscriptLine = { role: 'user' | 'assistant'; text: string };

export type VoiceAgentFormCaptureMessage = {
  type: 'form_capture';
  success?: boolean;
  parsed_fields?: Record<string, unknown>;
  error?: string;
};

export type VoiceAgentControlEventMessage = {
  type: 'event';
  name: string;
  success?: boolean;
  error?: string;
  payload?: Record<string, unknown>;
};

/**
 * Only spoken transcript lines are shown: plain text, or JSON with a string `text` field.
 * Other JSON (control/status, etc.) is ignored so raw blobs never appear in the UI.
 */
function parsePipecatDataChannelPayload(raw: string): {
  formCapture: VoiceAgentFormCaptureMessage | null;
  voiceEvent: VoiceAgentControlEventMessage | null;
  clientMessageAck: boolean;
  transcript: VoiceAgentTranscriptLine | null;
} {
  const trimmedRaw = raw.trim();
  if (!trimmedRaw) {
    return { formCapture: null, voiceEvent: null, clientMessageAck: false, transcript: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmedRaw);
  } catch {
    return {
      formCapture: null,
      voiceEvent: null,
      clientMessageAck: false,
      transcript: { role: 'assistant', text: trimmedRaw },
    };
  }

  if (typeof parsed === 'string') {
    const text = parsed.trim();
    return {
      formCapture: null,
      voiceEvent: null,
      clientMessageAck: false,
      transcript: text ? { role: 'assistant', text } : null,
    };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { formCapture: null, voiceEvent: null, clientMessageAck: false, transcript: null };
  }

  const o = parsed as VoiceAgentFormCaptureMessage &
    VoiceAgentControlEventMessage & {
      text?: unknown;
      role?: string;
      type?: string;
    };

  if (o.type === 'client_message_ack') {
    return { formCapture: null, voiceEvent: null, clientMessageAck: true, transcript: null };
  }
  if (o.type === 'form_capture') {
    return { formCapture: o, voiceEvent: null, clientMessageAck: false, transcript: null };
  }
  if (o.type === 'event' && typeof o.name === 'string' && o.name.trim()) {
    return {
      formCapture: null,
      voiceEvent: {
        type: 'event',
        name: o.name.trim(),
        success: o.success,
        error: o.error,
        payload: o.payload,
      },
      clientMessageAck: false,
      transcript: null,
    };
  }

  if (typeof o.text !== 'string') {
    return { formCapture: null, voiceEvent: null, clientMessageAck: false, transcript: null };
  }

  const text = o.text.trim();
  if (!text) {
    return { formCapture: null, voiceEvent: null, clientMessageAck: false, transcript: null };
  }

  let role: 'user' | 'assistant' = 'assistant';
  if (o.role === 'user' || o.role === 'assistant') {
    role = o.role;
  }
  return {
    formCapture: null,
    voiceEvent: null,
    clientMessageAck: false,
    transcript: { role, text },
  };
}

function attachPipecatTranscriptChannel(
  channel: RTCDataChannel,
  onTranscriptLine: ((line: VoiceAgentTranscriptLine) => void) | undefined,
  onFormCapture: ((message: VoiceAgentFormCaptureMessage) => void) | undefined,
  onVoiceEvent: ((message: VoiceAgentControlEventMessage) => void) | undefined,
  onClientMessageAck: (() => void) | undefined,
  logContext: string,
  refs?: VoiceAgentWebRtcRefs
): void {
  if (refs?.dataChannelRef) {
    refs.dataChannelRef.current = channel;
  }
  channel.onopen = () => {
    console.log(`${logContext} data channel open`);
    if (refs) flushPendingDataChannelMessages(refs, logContext);
  };
  channel.onmessage = (e) => {
    try {
      const raw = typeof e.data === 'string' ? e.data : String(e.data);
      if (onFormCapture || onVoiceEvent) {
        console.log(`${logContext} data channel message`, raw);
      }
      const { formCapture, voiceEvent, clientMessageAck, transcript } =
        parsePipecatDataChannelPayload(raw);
      if (clientMessageAck) {
        console.log(`${logContext} client_message_ack`);
        onClientMessageAck?.();
        return;
      }
      if (formCapture) {
        console.log(`${logContext} form_capture response`, formCapture);
        if (formCapture.parsed_fields) {
          console.log(
            `${logContext} form_capture parsed_fields (JSON)`,
            JSON.stringify(formCapture.parsed_fields, null, 2)
          );
        }
        onFormCapture?.(formCapture);
        return;
      }
      if (voiceEvent) {
        console.log(`${logContext} voice event`, voiceEvent);
        onVoiceEvent?.(voiceEvent);
        return;
      }
      if (transcript) onTranscriptLine?.(transcript);
    } catch (err) {
      console.warn(`${logContext} data channel message parse error`, err);
    }
  };
}

/** Sends a JSON message to the voice agent over the pipecat data channel (queues until open). */
export function sendVoiceAgentDataChannelMessage(
  refs: VoiceAgentWebRtcRefs,
  message: Record<string, unknown>,
  logContext = '[VoiceAgent]'
): boolean {
  const payload = JSON.stringify(message);
  const channel = refs.dataChannelRef?.current;
  if (channel?.readyState === 'open') {
    try {
      channel.send(payload);
      console.log(`${logContext} data channel send`, payload);
      return true;
    } catch (err) {
      console.warn(`${logContext} data channel send failed`, err);
      return false;
    }
  }
  const queue = refs.pendingDataChannelMessagesRef?.current;
  if (queue) {
    queue.push(payload);
    console.log(`${logContext} data channel queued (not open yet)`, payload);
    return true;
  }
  console.warn(`${logContext} data channel not open; cannot send`, message);
  return false;
}

/** Mid-call inject of already-known form values (skips re-asking). */
export function sendVoiceAgentFormUpdate(
  refs: VoiceAgentWebRtcRefs,
  fields: Record<string, string>,
  options?: { runLlm?: boolean; logContext?: string }
): boolean {
  const run_llm = options?.runLlm ?? true;
  const logContext = options?.logContext ?? '[VoiceAgent]';
  if (Object.keys(fields).length === 0) return false;
  return sendVoiceAgentDataChannelMessage(
    refs,
    {
      type: 'form_update',
      payload: { fields },
      run_llm,
    },
    logContext
  );
}

export type StartVoiceAgentWebRtcParams = {
  assistantId: string;
  token: string | null;
  t: TFunction;
  postWebRtcSession: PostWebRtcSessionMutation;
  postOffer: PostOfferMutation;
  refs: VoiceAgentWebRtcRefs;
  /** Prefix for console warnings, e.g. `[VoiceAgentFloatingMic]` */
  logContext: string;
  onConnected: () => void;
  onDisconnected: () => void;
  /** Optional hook when remote audio track unmutes (e.g. success toast). */
  onRemoteTrackUnmute?: () => void;
  /** Live lines from the voice agent data channel (same protocol as test.html / pipecat). */
  onTranscriptLine?: (line: VoiceAgentTranscriptLine) => void;
  /** `form_capture` events from the voice agent (form filling). */
  onFormCapture?: (message: VoiceAgentFormCaptureMessage) => void;
  /** Control events from the voice agent (`type: "event"`). */
  onVoiceEvent?: (message: VoiceAgentControlEventMessage) => void;
  /** Fired when the agent acknowledges a client data-channel message. */
  onClientMessageAck?: () => void;
  /** Passed to Lead `agentcall/web_RTC` (defaults to `{ extra: {} }`). */
  customerDetails?: {
    extra?: Record<string, unknown>;
    name?: string;
    email?: string;
    history?: string;
  };
};

/**
 * Establishes browser WebRTC voice session with the voice agent (session + offer/answer).
 * Shows toasts on failure and runs cleanup + onDisconnected when appropriate.
 */
export async function startVoiceAgentWebRtcSession(
  params: StartVoiceAgentWebRtcParams
): Promise<void> {
  const {
    assistantId,
    token,
    t,
    postWebRtcSession,
    postOffer,
    refs,
    logContext,
    onConnected,
    onDisconnected,
    onRemoteTrackUnmute,
    onTranscriptLine,
    onFormCapture,
    onVoiceEvent,
    onClientMessageAck,
    customerDetails,
  } = params;

  const fail = (toastMessage: string) => {
    CtyShowToast(checkType.error, toastMessage);
    cleanupVoiceAgentWebRtc(refs, onDisconnected);
  };

  if (!assistantId || !token) {
    CtyShowToast(checkType.error, t('Authentication required. Please log in.'));
    return;
  }

  try {
    const sessionRes = await postWebRtcSession({
      assistant_id: assistantId,
      phonenumber: '',
      customer_details: customerDetails ?? { extra: {} },
    }).unwrap();
    const sessionId = sessionRes?.session_id;
    if (!sessionId) {
      fail(t('Session creation failed. No session ID returned.'));
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    refs.localStreamRef.current = stream;
    const audioTrack = stream.getAudioTracks()[0];

    const iceServers = getIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    refs.peerConnectionRef.current = pc;

    pc.addTransceiver(audioTrack, { direction: 'sendrecv' });

    // Create data channel BEFORE offer (required for reliable mid-call form_update / events).
    const pipecatChannel = pc.createDataChannel('pipecat');
    attachPipecatTranscriptChannel(
      pipecatChannel,
      onTranscriptLine,
      onFormCapture,
      onVoiceEvent,
      onClientMessageAck,
      logContext,
      refs
    );
    pc.ondatachannel = (event) => {
      attachPipecatTranscriptChannel(
        event.channel,
        onTranscriptLine,
        onFormCapture,
        onVoiceEvent,
        onClientMessageAck,
        logContext,
        refs
      );
    };

    pc.ontrack = (e) => {
      const track = e.track;
      track.enabled = true;
      track.onunmute = () => {
        onRemoteTrackUnmute?.();
        if (refs.audioRef.current) {
          refs.audioRef.current.volume = 1;
          refs.audioRef.current
            .play()
            .catch((err) => console.warn(`${logContext} play on unmute failed:`, err));
        }
      };
      const remoteStream = e.streams[0] ?? new MediaStream([track]);
      if (refs.audioRef.current) {
        refs.audioRef.current.srcObject = remoteStream;
        refs.audioRef.current.volume = 1;
        refs.audioRef.current.muted = false;
        refs.audioRef.current
          .play()
          .catch((err) => console.warn(`${logContext} Audio autoplay failed:`, err));
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        onConnected();
      }
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        onDisconnected();
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGatheringComplete(pc, 2000);

    const localDesc = pc.localDescription;
    if (!localDesc?.sdp) {
      fail(t('WebRTC offer failed. Please try again.'));
      return;
    }

    const answerRes = await postOffer({
      sdp: localDesc.sdp,
      type: localDesc.type as string,
      session_id: sessionId,
    }).unwrap();

    const sdp = answerRes.sdp ?? answerRes.answer?.sdp;
    const type = answerRes.type ?? answerRes.answer?.type;
    if (!sdp || !type) {
      throw new Error('Invalid offer response - missing sdp or type');
    }
    await pc.setRemoteDescription(
      new RTCSessionDescription({
        type: type as RTCSdpType,
        sdp,
      })
    );

    const opened = await waitForDataChannelOpen(pipecatChannel);
    if (opened) {
      flushPendingDataChannelMessages(refs, logContext);
    } else {
      console.warn(`${logContext} data channel did not open in time; mid-call sends may queue`);
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      CtyShowToast(
        checkType.error,
        t('Microphone access denied. Please enable microphone in browser settings.')
      );
    } else {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      const message = detail ?? (err as Error)?.message ?? t('Webcall failed.');
      CtyShowToast(checkType.error, message);
    }
    cleanupVoiceAgentWebRtc(refs, onDisconnected);
  }
}
